import React, { useState } from 'react';
import BaileyAvatar from './bailey/BaileyAvatar';
import { STATE_LABELS } from './bailey/looks';
import { greeting, firstName, threadTitle, when, dueOn, pounds } from './bailey/format';
import { hapticTick } from '../../_utils/haptics';
import { Composer, Icon } from './BaileyBits';
import { messageOf } from './ApprovalCard';
import FrontDeskCard from './frontdesk/FrontDeskCard';
import { SoulMark } from './frontdesk/SoulCards';
import { soulSummary, soulHue } from './frontdesk/soul';
import { useNeeds, useNeedActions, NeedsList, NeedsLine } from './frontdesk/NeedsYou';

/**
 * The Bailey tab's home screen.
 *
 * One calm column, in the order an owner needs it: say what you want, then
 * anything waiting on you, what Bailey noticed, what's in progress, recent
 * conversations, and finally the full range of things Bailey can take on.
 */

// People shown under Needs you before See all.
const HOME_ROWS = 3;

const KIND = {
    campaign: { icon: '📣', label: 'Message' },
    automation: { icon: '💌', label: 'Automation' },
    journey: { icon: '💌', label: 'Automation' }
};

export function Section({ label, count, action, children, className = '' }) {
    return (
        <section className={`bly-sec ${className}`}>
            <div className="bly-sec-head">
                <h2 className="bly-label">
                    {label}
                    {count > 0 && <span className="bly-count">{count}</span>}
                </h2>
                {action}
            </div>
            {children}
        </section>
    );
}

export function AskCard({ ask, busy, onApprove, onReject }) {
    const p = (ask.action && ask.action.preview) || {};
    const bits = [
        p.recipients != null && `${p.recipients} ${p.recipients === 1 ? 'person' : 'people'}`,
        p.cost != null && pounds(p.cost)
    ].filter(Boolean).join(' · ');
    const words = messageOf(p);
    return (
        <div className="bly-card bly-ask-card">
            <div className="bly-ask-copy">
                <b>{ask.title}</b>
                {(bits || ask.body) && <span>{bits || ask.body}</span>}
                {/* What's being sent, so the OK is to the words and not only the numbers */}
                {words && <q className="bly-ask-message">{words}</q>}
            </div>
            <div className="bly-actions">
                <button type="button" className="bly-btn is-ghost" disabled={busy} onClick={() => { hapticTick(); onReject(ask._id); }}>Not now</button>
                <button type="button" className="bly-btn is-primary" disabled={busy} onClick={() => { hapticTick(); onApprove(ask._id); }}>
                    {busy ? 'Approving…' : ask.kind === 'approve' ? 'Approve' : 'Yes'}
                </button>
            </div>
        </div>
    );
}

export function Row({ icon, title, sub, right, onClick, tag, go }) {
    const inner = (
        <>
            <span className="bly-tile" aria-hidden="true">{icon}</span>
            <span className="bly-row-copy">
                <b>{title}{tag && <em className="bly-tag">{tag}</em>}</b>
                {sub && <small>{sub}</small>}
            </span>
            {right !== undefined ? right : <span className="bly-chev"><Icon.Chevron /></span>}
        </>
    );
    const cls = `bly-row${go ? ' has-go' : ''}`;
    return onClick
        ? <button type="button" className={cls} onClick={() => { hapticTick(); onClick(); }}>{inner}</button>
        : <div className={cls}>{inner}</div>;
}

export default function BaileyHome(props) {
    const {
        status, look, baileyState, avatarActivity, motionPaused, canAct,
        draft, setDraft, onSend, sending, inputRef, onFocus, onBlur,
        waiting, busyApproval, onApprove, onReject,
        noticed, onNoticed,
        goals, drafts, pill, onOpenGoal, onOpenDraft,
        recent, isPinned, onResume, onAllConversations,
        ideas, onIdea, onHowIWork,
        frontDesk, onCall, onFrontDesk, onFinishSetup, onAllMessages, history, salonName,
        onStyle, onSoul
    } = props;
    const [allWork, setAllWork] = useState(false);
    const staff = status && status.principalType === 'employed-staff';
    // Messages Bailey took, one row per person, the most pressing first.
    const people = useNeeds(frontDesk);
    const needs = useNeedActions({ fd: frontDesk, groups: people, history, salonName });
    const messageCount = frontDesk ? frontDesk.open.length : 0;

    const work = [];
    if (pill) {
        work.push({
            key: 'setup',
            icon: '🧭',
            title: pill.title,
            sub: pill.detail,
            right: <span className="bly-row-go">{pill.go}</span>,
            go: true,
            onClick: pill.open
        });
    }
    goals.forEach((g) => {
        const c = g.lastCheckin;
        const dir = c && c.deltaPct > 0 ? 'up' : c && c.deltaPct < 0 ? 'down' : 'flat';
        const base = g.baseline || {};
        const due = dueOn(g.nextCheckAt);
        work.push({
            key: `goal-${g._id}`,
            icon: '🎯',
            title: g.title,
            // Never a hopeful 0% before anything has been measured.
            sub: c ? c.summary : `Started from ${base.value} ${base.metric}.${due ? ` First check ${due}.` : ''}`,
            right: c ? <span className={`bly-delta is-${dir}`}>{dir === 'flat' ? 'No change' : `${c.deltaPct > 0 ? '+' : ''}${c.deltaPct}%`}</span> : undefined,
            onClick: () => onOpenGoal(g)
        });
    });
    drafts.slice().sort((a, b) => String(b.at || '').localeCompare(String(a.at || ''))).forEach((d) => {
        const k = KIND[d.kind] || { icon: '📝', label: 'Draft' };
        work.push({
            key: `draft-${d.kind}-${d.id}`,
            icon: k.icon,
            title: d.title,
            sub: /switched off/i.test(d.detail || '') ? `${k.label}, made and switched off` : [d.detail, 'Not sent yet'].filter(Boolean).join(' · '),
            onClick: () => onOpenDraft(d)
        });
    });
    const shownWork = allWork ? work : work.slice(0, 3);

    return (
        <div className="bly-home">
            <header className="bly-hello">
                <div className="bly-hello-bailey">
                    <BaileyAvatar appearance={look} state={baileyState} activity={avatarActivity} paused={motionPaused} />
                    {onStyle && (
                        <button type="button" className="bly-style-btn" onClick={() => { hapticTick(); onStyle(); }} aria-label="Style Bailey" title="Style Bailey">
                            <Icon.Brush size={15} />
                        </button>
                    )}
                </div>
                <div className="bly-hello-copy">
                    <h1>{greeting(firstName())}</h1>
                    <p>
                        {!canAct
                            ? (staff
                                ? 'Ask me about your day, your clients or your takings and I’ll look it up.'
                                : 'Ask me anything about your business and I’ll look it up.')
                            : staff
                                ? 'Tell me what you need and I’ll do the legwork.'
                                : 'Tell me what needs doing and I’ll do the legwork. Nothing reaches a client until you say so.'}
                    </p>
                    <div className="bly-hello-meta">
                        <span className={`bly-status is-${baileyState}`}><i />{onCall ? 'On the phone with a client' : STATE_LABELS[baileyState]}</span>
                        {/* Bailey's soul, where it's seen: how Bailey talks, one tap to change it. */}
                        {onSoul && frontDesk && frontDesk.ready && (
                            <button type="button" className={`bly-soul-chip${motionPaused ? ' is-still' : ''}`} onClick={() => { hapticTick(); onSoul(); }}
                                aria-label={`Bailey’s soul: ${soulSummary(frontDesk.soul)}. Change how Bailey talks`}>
                                <SoulMark hue={soulHue(frontDesk.soul)} size={15} />
                                <small>Soul</small>
                                <span>{soulSummary(frontDesk.soul)}</span>
                                <Icon.Chevron size={13} />
                            </button>
                        )}
                    </div>
                </div>
            </header>

            <div className="bly-ask">
                <Composer
                    variant="home"
                    value={draft}
                    onChange={setDraft}
                    onSend={() => onSend()}
                    busy={sending}
                    inputRef={inputRef}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    placeholder={canAct ? 'Ask Bailey, or tell it what to do…' : staff ? 'Ask Bailey about your day…' : 'Ask Bailey about your business…'}
                />
                <div className="bly-promises">
                    {canAct ? (
                        <>
                            <span><Icon.Tick size={13} />You approve anything clients see</span>
                            <span><Icon.Tick size={13} />Costs shown before you say yes</span>
                        </>
                    ) : (
                        <span><Icon.Tick size={13} />Read only for now</span>
                    )}
                    <button type="button" className="bly-link" onClick={() => { hapticTick(); onHowIWork(); }}>How I work</button>
                </div>
            </div>

            {/* Approvals first (Bailey can't go on without them), then three
                people at most, so the rest of the home is in reach. Kept for
                the Undo after the last one is done. */}
            {(!!(waiting.length + messageCount) || !!needs.undoBar) && (
                <Section label="Needs you" count={waiting.length + messageCount} className="is-needs">
                    {!!people.length && <NeedsLine groups={people} />}
                    {needs.undoBar}
                    {!!waiting.length && (
                        <div className="bly-stack">
                            {waiting.map((w) => (
                                <AskCard key={w._id} ask={w} busy={busyApproval === w._id} onApprove={onApprove} onReject={onReject} />
                            ))}
                        </div>
                    )}
                    {!!people.length && (
                        <NeedsList fd={frontDesk} groups={people} limit={HOME_ROWS - waiting.length} onSeeAll={onAllMessages} act={needs.act} />
                    )}
                </Section>
            )}
            {needs.panels}

            {/* Bailey's second job, set up or not, where it's easy to find. */}
            <FrontDeskCard fd={frontDesk} onCall={onCall} onOpen={onFrontDesk} onSetUp={onFinishSetup} />

            {!!noticed.length && (
                <Section label="I noticed">
                    <div className="bly-card bly-list">
                        {noticed.map((n) => (
                            <Row
                                key={n.id}
                                icon={n.icon || '💡'}
                                title={n.title}
                                sub={n.why}
                                tag={n.source === 'example' ? 'Example' : null}
                                right={<span className="bly-row-go">{n.cta || 'Let’s do it'}</span>}
                                go
                                onClick={() => onNoticed(n)}
                            />
                        ))}
                    </div>
                </Section>
            )}

            {!!work.length && (
                <Section label="On the go">
                    <div className="bly-card bly-list">
                        {shownWork.map(({ key, ...r }) => <Row key={key} {...r} />)}
                        {work.length > 3 && (
                            <button type="button" className="bly-list-more" onClick={() => { hapticTick(); setAllWork((v) => !v); }}>
                                {allWork ? 'Show less' : `Show all your work (${work.length})`}
                            </button>
                        )}
                    </div>
                </Section>
            )}

            {!!recent.length && (
                <Section label="Recent conversations">
                    <div className="bly-card bly-list">
                        {recent.slice(0, 3).map((r) => (
                            <button key={r._id} type="button" className="bly-thread-row" onClick={() => { hapticTick(); onResume(r._id); }}>
                                <span className="bly-thread-title">
                                    {isPinned && isPinned(r._id) && <span className="bly-row-pin" aria-label="Pinned"><Icon.Pin size={13} filled /></span>}
                                    {threadTitle(r)}
                                </span>
                                <small>{when(r.lastMessageAt || r.createdAt)}</small>
                                <span className="bly-chev"><Icon.Chevron /></span>
                            </button>
                        ))}
                        <button type="button" className="bly-list-more" onClick={() => { hapticTick(); onAllConversations(); }}>All conversations</button>
                    </div>
                </Section>
            )}

            {!!ideas.length && (
                <section className="bly-ideas" aria-labelledby="bly-ideas-title">
                    <div className="bly-ideas-head">
                        <h2 id="bly-ideas-title">{staff ? 'Things I can help with' : 'Things I can take off your plate'}</h2>
                        <p>Tap one to start. I’ll ask for anything I need.</p>
                    </div>
                    {ideas.map((g) => (
                        <div className="bly-idea-group" key={g.group}>
                            <h3>{g.group}</h3>
                            {g.items.map((i) => (
                                <button key={i.id} type="button" className={`bly-idea${i.live ? ' is-live' : ''}`} onClick={() => { hapticTick(); onIdea(i); }}>
                                    <span className="bly-tile is-idea" aria-hidden="true">{i.icon}</span>
                                    <span className="bly-idea-copy">
                                        <b>{i.title}</b>
                                        <small className="is-long">{i.how}</small>
                                        <small className="is-short">{i.short || i.how}</small>
                                    </span>
                                </button>
                            ))}
                        </div>
                    ))}
                </section>
            )}
        </div>
    );
}
